// Ported from nenda-autoplayer's src/components/Playback/ImagePlayer.tsx.
// RN `Image`/`Animated`/`View` -> plain DOM `<img>` with a CSS opacity
// transition standing in for `Animated.timing`. Logic (the timed-playback
// hook, the debug countdown overlay) is unchanged.
//
// DELIBERATE DELTA: upstream runs every image through `scaleImage()`
// (src/lib/utils/imageScaler.ts), which rewrites the src through Nenda's
// public Cloudflare Image-Resizing endpoint (`static.nenda.com/cdn-cgi/image`).
// That endpoint resizes images from the ZONES IT IS CONFIGURED to fetch as a
// remote origin - verified as unconfirmed for Foyer's own asset host
// (`foyer.nenda.com`/`foyer-assets`), and Foyer's current renderer
// (SignageSurface.tsx) never does this rewrite either, rendering every
// signage image at its original locator. Porting the rewrite unverified risks
// silently breaking image loading fleet-wide the moment this ships - so this
// port renders `src` as-is, matching Foyer's current, working behavior. Wiring
// scaling back in is a follow-up, gated on confirming the resizer zone accepts
// Foyer's asset host as a remote origin.
import React, { useRef, useEffect, useCallback } from "react";
import { useTimedPlayback } from "./useTimedPlayback";
import CountdownOverlay from "./CountDownOverlay";
import { PlaybackErrorLevel } from "../types/error";

interface ImagePlayerProps {
  duration: number;
  src: string;
  onCompleted: () => void;
  onError: (error: Error, level: PlaybackErrorLevel) => void;
  isDebugModeActive?: boolean;
}

const ImagePlayer: React.FC<ImagePlayerProps> = ({
  duration,
  src,
  onCompleted,
  onError,
  isDebugModeActive = false,
}) => {
  const { remainingSeconds } = useTimedPlayback({ duration, onCompleted });
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const el = imgRef.current;
    if (el) el.style.opacity = "0";
  }, [src]);

  const handleOnLoad = useCallback(() => {
    const el = imgRef.current;
    if (el) el.style.opacity = "1";
  }, []);

  const handleOnError = useCallback(() => {
    onError(
      new Error(`Failed to load image: ${src}`),
      PlaybackErrorLevel.FAILURE
    );
  }, [onError, src]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <img
        ref={imgRef}
        src={src}
        alt=""
        data-testid="signage-image"
        onLoad={handleOnLoad}
        onError={handleOnError}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          opacity: 0,
          transition: "opacity 300ms",
        }}
      />
      {isDebugModeActive && (
        <CountdownOverlay remainingSeconds={remainingSeconds} />
      )}
    </div>
  );
};

export default React.memo(ImagePlayer);
